import styles from './CmsBusinessJourneyWizard.module.scss';
import TypeSelector from './StepType/TypeSelector';
import { useLocalStorage, useTracking } from '@components/hooks';
import {
  Alert,
  Button,
  Content,
  Heading,
  Pill,
  getHeadingTag,
} from '@wienenergiegithub/ui-next';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

const filterCards = (cardConditions, answers = []) =>
  answers?.every(answer => cardConditions.split('-').includes(answer.keyword));

// reset all selected answers
const handleReset = (stepNumber, setStepNumber, storage, setStorage, track) => {
  setStepNumber(0);
  setStorage({
    answers: [],
    selectedProducts: [],
    pageLink: '',
    emailSent: false,
  });

  // track reset of all answers
  track({
    event: 'B2BProduktWegweiser_reset',
    answers: storage?.answers.map(answ => answ?.title.trim()),
    products: storage?.selectedProducts.map(product => product?.link),
    step: stepNumber,
  });
};

const handleAnswerPillClick = (index, setStepNumber, storage, setStorage) => {
  setStorage({
    answers: storage.answers.slice(0, index),
    selectedProducts: [],
    pageLink: '',
    emailSent: false,
  });
  setStepNumber(index);
};

const CmsBusinessJourneyWizard = ({ headingLevel = 2, steps }) => {
  const [storage, setStorage] = useLocalStorage('items', {
    answers: [],
    selectedProducts: [],
    pageLink: '',
    emailSent: false,
  });
  const [stepNumber, setStepNumber] = useState(0);
  const [filteredCards, setFilteredCards] = useState(
    steps[stepNumber]?.cards.filter(card =>
      filterCards(card.conditions, storage?.answers),
    ),
  );
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const track = useTracking();

  useEffect(() => {
    setFilteredCards(
      steps[stepNumber]?.cards.filter(card =>
        filterCards(card.conditions, storage?.answers),
      ),
    );

    // Tracking for each answer / step
    if (stepNumber > 0) {
      track({
        event: 'B2BProduktWegweiser',
        answers: storage?.answers.map(answ => answ.title.trim()),
        step: stepNumber,
      });
    }
  }, [stepNumber]);

  useEffect(() => {
    if (filteredCards.length) {
      setShowErrorNotification(false);
    }

    if (filteredCards.length === 0 && steps[stepNumber + 1]) {
      setStepNumber(currentStep => currentStep + 1);
    } else if (filteredCards.length === 0 && !steps[stepNumber + 1]) {
      setShowErrorNotification(true);
    }
  }, [filteredCards]);

  useEffect(() => {
    setStepNumber(
      storage?.answers?.length > 0 ? (storage?.answers?.length || 1) - 1 : 0,
    );
  }, []);

  return (
    // id is used to scroll to, no ref used because data is passed by storage to businessJourneyOverlay
    <div className={styles.recommenderContainer} id="business-wizard">
      <div>
        {steps[stepNumber]?.title && (
          <Heading as={getHeadingTag(headingLevel)} noSpacing size="h2">
            {steps[stepNumber].title}
          </Heading>
        )}
        {steps[stepNumber]?.subtitle && (
          <Content className={styles.subtitle} noSpacing>
            {steps[stepNumber].subtitle}
          </Content>
        )}
      </div>
      {storage?.answers?.length > 0 && (
        <div>
          <div className={styles.answersContainer}>
            <Heading
              as={getHeadingTag(headingLevel + 1)}
              className={styles.noWrap}
              noSpacing
              size="h4"
            >
              Ihre Auswahl
            </Heading>
            {/* Showing selected answers */}
            <div className={styles.pillContainer}>
              {storage?.answers?.map((answer, index) => (
                <Pill
                  className={styles.noWrap}
                  glyph="times"
                  isActive
                  key={answer.keyword + answer.title}
                  onClick={() =>
                    handleAnswerPillClick(
                      index,
                      setStepNumber,
                      storage,
                      setStorage,
                    )
                  }
                >
                  {answer.title}
                </Pill>
              ))}
            </div>
          </div>
          <Button
            className={styles.resetButton}
            onClick={() =>
              handleReset(stepNumber, setStepNumber, storage, setStorage, track)
            }
            variant="link"
          >
            Zurücksetzen
          </Button>
        </div>
      )}
      {steps[stepNumber]?.question && filteredCards.length > 0 && (
        <Heading as={getHeadingTag(headingLevel + 1)} noSpacing size="h4">
          {steps[stepNumber].question}
        </Heading>
      )}
      <TypeSelector
        cards={filteredCards}
        headingLevel={headingLevel + 2}
        setStepNumber={setStepNumber}
        setStorage={setStorage}
        stepType={steps[stepNumber].stepType}
        storage={storage}
      />
      {showErrorNotification && (
        <div className={styles.alertContainer}>
          <Alert
            details="Bitte gehen Sie zurück auf die <a href='https://www.wienenergie.at/business/produkte/'>Produktseite.</a>"
            fontAwesomeIcon="siren-on"
            headingLevel={headingLevel + 1}
            title="Es ist ein Fehler aufgetreten"
            type="warning"
          />
        </div>
      )}
    </div>
  );
};

CmsBusinessJourneyWizard.propTypes = {
  headingLevel: PropTypes.number,
  // eslint-disable-next-line react/forbid-prop-types
  steps: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default CmsBusinessJourneyWizard;
